import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useAnimationControls, useReducedMotion } from "motion/react";
import type { Decision, Job } from "../../shared/types.ts";
import { Button, MarkLoader, Spinner, StatusBadge, cn, type ButtonProps, type Tone } from "../ui/index.ts";
import { ApiError, decide, errorText, undecide } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { askUpgrade, isLimit } from "./UpgradeModal.tsx";

/** A friendly read of the feed's rank score, or null for a weak one. */
const matchLabel = (score: number | undefined): string | null =>
  score === undefined ? null : score >= 6 ? "Top match" : score >= 4 ? "Strong match" : score >= 2.5 ? "Good match" : null;

/** The feed's rank as a small tinted badge. Renders nothing without a score worth showing. */
export function MatchChip({ score, className }: { score: number | undefined; className?: string }) {
  const label = matchLabel(score);
  if (!label) return null;
  const tone: Tone = score! >= 6 ? "brand" : score! >= 4 ? "mint" : "gray";
  return (
    <StatusBadge tone={tone} className={className}>
      {label}
    </StatusBadge>
  );
}

/** 0 to 1 ghost score as a risk level, or null when the engine has no read. */
export function ghostRisk(ghost: number | null | undefined): { label: string; tone: Tone } | null {
  if (ghost == null) return null;
  if (ghost >= 0.6) return { label: "High", tone: "red" };
  if (ghost >= 0.3) return { label: "Some", tone: "amber" };
  return { label: "Low", tone: "mint" };
}

/** What the apply button says: the engine fills it, or it opens on the company site. */
export const applyLabel = (job: Pick<Job, "canAutoApply">) => (job.canAutoApply ? "Apply" : "Apply on company site");

/** Icon cross-fade for a state change (saved, sending). */
export const swap = {
  initial: { opacity: 0, scale: 0.25, filter: "blur(4px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, scale: 0.25, filter: "blur(4px)" },
  transition: { type: "spring" as const, duration: 0.3, bounce: 0 },
};

/** The check's draw and then a beat to read it, before the caller moves on. */
const SENT_HOLD = 820;

const SPARKS = 10;
const SPARK_TONES = ["bg-primary", "bg-accent", "bg-ok", "bg-primary", "bg-accent"];

/** A ring that pops off the button and sparks flung out around it, drawn over the page so no row clips them. */
function Burst({ at }: { at: DOMRect }) {
  const x = at.left + at.width / 2;
  const y = at.top + at.height / 2;
  return createPortal(
    <span aria-hidden className="pointer-events-none fixed z-[80]" style={{ left: x, top: y }}>
      <motion.span
        className="absolute rounded-control border-2 border-ok"
        style={{ width: at.width, height: at.height, left: -at.width / 2, top: -at.height / 2 }}
        initial={{ opacity: 0.9, scale: 0.9 }}
        animate={{ opacity: 0, scale: 1.35 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
      {Array.from({ length: SPARKS }, (_, i) => {
        const a = (i / SPARKS) * Math.PI * 2;
        const r = at.width / 2 + 14 + (i % 3) * 5;
        return (
          <motion.span
            key={i}
            className={cn("absolute -left-[3px] -top-[3px] size-1.5 rounded-xs", SPARK_TONES[i % SPARK_TONES.length])}
            initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
            animate={{ x: Math.cos(a) * r, y: Math.sin(a) * (at.height / 2 + 16), scale: 0.3, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.2, 0.8, 0.3, 1] }}
          />
        );
      })}
    </span>,
    document.body,
  );
}

/**
 * Apply on a job Headcount sends. A press spins while `send` records it; once the server takes it the button turns
 * green and draws a check with a burst around it, holds, then calls `onSent`. A refusal (a plan or daily limit, an error) shakes it back to
 * its label and leaves the upgrade modal or toast `send` showed. Clicks stop here, so it can sit inside a clickable row.
 */
export function SendButton({
  send,
  onSent,
  className,
  children = "Apply",
  ...rest
}: Omit<ButtonProps, "onClick" | "loading"> & { send: (from: HTMLButtonElement) => Promise<boolean>; onSent?: () => void }) {
  const [phase, setPhase] = useState<"idle" | "busy" | "sent">("idle");
  const [burst, setBurst] = useState<DOMRect | null>(null);
  const controls = useAnimationControls();
  const still = useReducedMotion();
  const land = (ok: boolean, el: HTMLButtonElement) => {
    if (!ok) {
      setPhase("idle");
      if (!still) controls.start({ x: [0, -7, 6, -4, 2, 0], transition: { duration: 0.38, ease: "easeOut" } });
      return;
    }
    setPhase("sent");
    if (!still) {
      controls.start({ scale: [1, 1.05, 1], transition: { duration: 0.34, ease: "easeOut" } });
      setBurst(el.getBoundingClientRect());
    }
    setTimeout(() => onSent?.(), SENT_HOLD);
  };
  return (
    <Button
      {...rest}
      animate={controls}
      aria-busy={phase === "busy" || undefined}
      className={cn(className, phase === "sent" && "bg-ok text-white shadow-soft hover:bg-ok")}
      onClick={(e) => {
        e.stopPropagation();
        if (phase !== "idle") return;
        setPhase("busy");
        const el = e.currentTarget;
        send(el).then(
          (ok) => land(ok, el),
          () => land(false, el),
        );
      }}
    >
      <span className={cn("inline-flex items-center gap-2", phase !== "idle" && "invisible")}>{children}</span>
      {phase !== "idle" && (
        <span className="absolute inset-0 grid place-items-center">
          {phase === "busy" ? (
            <Spinner label="Sending" className="size-[18px]" />
          ) : (
            <motion.svg
              viewBox="0 0 24 24"
              fill="none"
              role="img"
              aria-label="Sending"
              initial={still ? false : { scale: 0.6 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 520, damping: 22 }}
              className="size-5"
            >
              <motion.path
                d="M5 12.5 9.5 17 19 7.5"
                stroke="currentColor"
                strokeWidth={2.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={still ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </motion.svg>
          )}
        </span>
      )}
      {burst && <Burst at={burst} />}
    </Button>
  );
}

/** The house loading state for a page, a sheet or (with `row`) a section. */
export function Loading({ label, row }: { label: string; row?: boolean }) {
  return row ? <MarkLoader layout="row" label={label} className="py-2" /> : <MarkLoader label={label} />;
}

const failText = (e: unknown, job: Job) => (e instanceof ApiError && e.error === "job_closed" ? `${job.company} closed this posting.` : errorText(e));

/**
 * Records a decision with the counters and toasts the app expects: an on-site job opens on the company site and counts as
 * applied straight away, an auto-apply job counts once the server takes it, and a save says so. Calling it again with the
 * same decision undoes it.
 */
export function useDecide() {
  const app = useApp();
  return useCallback(
    (job: Job, d: Decision, current: Decision | null, from?: Element | null, quiet = false): Promise<Decision | null> => {
      const auto = !!job.canAutoApply;
      const unapply = () => app.unapplied(auto);
      // Counts are read back once the write lands; a fixed delay raced slow writes and showed stale counts.
      const settle = <T,>(p: Promise<T>) => (d === "apply" || current === "apply" ? p.finally(app.refreshStats) : p);
      if (current === d) {
        if (d === "apply") unapply();
        return settle(undecide(job.id)).then(
          () => null,
          () => {
            app.toast("Undo didn't save. Try again.", "bad");
            return current;
          },
        );
      }
      if (current === "apply") unapply();
      const early = d === "apply" && !auto;
      if (early) app.applied(from);
      return settle(decide(job, d)).then(
        (r) => {
          if (r.skipped) {
            const limit = isLimit(r.skipped) ? r.skipped : null;
            if (early && limit) unapply();
            if (limit) askUpgrade(limit);
            else app.toast(`${r.skippedLabel.replace(/\.$/, "")}.`);
            // A cap records nothing, so the job keeps the decision it had and its row comes back; other skips still count as applied.
            return limit ? current : d;
          }
          if (d === "apply" && auto) app.applied(from, true);
          if (d === "save" && !quiet) app.toast("Saved.");
          return d;
        },
        (e) => {
          if (early) unapply();
          if (e instanceof ApiError && isLimit(e.error)) askUpgrade(e.error);
          else app.toast(failText(e, job), "bad");
          return current;
        },
      );
    },
    [app],
  );
}

/** A pill toggle for chip groups and filter rows. */
export function Pill({
  on,
  onClick,
  children,
  className,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "relative inline-flex h-9 shrink-0 items-center gap-1.5 rounded-control px-3.5 text-sm font-semibold transition-[background-color,color,box-shadow,scale] duration-150 active:scale-[0.94]",
        "before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']",
        on ? "bg-primary-soft text-primary-ink ring-1 ring-primary/50" : "bg-surface text-ink-soft ring-1 ring-line-strong hover:bg-sunk hover:text-ink",
        className,
      )}
    >
      {children}
    </button>
  );
}
